import { ApplicationConfig, importProvidersFrom, isDevMode, provideAppInitializer } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  PreloadAllModules,
  provideRouter,
  UrlSerializer,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withPreloading,
} from '@angular/router';
import { LowerCaseUrlSerializer } from './tools/lower.case.url.serializer';
import { MAT_DATE_LOCALE, MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import { TINYMCE_SCRIPT_SRC, EditorModule } from '@tinymce/tinymce-angular';
import { CurrencyMaskModule } from 'ng2-currency-mask';
// Angular Material modules
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { MatStepperModule, MatStepperIntl } from '@angular/material/stepper';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatTreeModule } from '@angular/material/tree';
// CDK modules
import { A11yModule } from '@angular/cdk/a11y';
import { BidiModule } from '@angular/cdk/bidi';
import { ObserversModule } from '@angular/cdk/observers';
import { OverlayModule } from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import { PortalModule } from '@angular/cdk/portal';
import { DragDropModule } from '@angular/cdk/drag-drop';
// QM
import { FeedbackService } from './service/feedback.service';
import { QmadsService } from './service/qmads.service';
import { ApiAddressService } from './service/api.address.service';
import { SysConfigService } from './service/sys.config';
import { LoginService } from './service/login.service';
import { BlogService } from './service/blog.service';
import { DataLoaderService } from './service/data.loader.service';
import { DataLoader } from './service/data.loader';
import { QuoteService } from './service/quote.service';
import { QuoteStepClient } from './service/quote/quote.step.client';
import { QuoteStepBenefit } from './service/quote/quote.step.benefit';
import { QuoteStepCompare } from './service/quote/quote.step.compare';
import { QuoteStepCompareChart } from './service/quote/quote.step.compare.chart';
import { QuoteStepCompareDocument } from './service/quote/quote.step.compare.document';
import { QuoteStepCompareResearch } from './service/quote/quote.step.compare.research';
import { QuoteStepCompareCheckmonster } from './service/quote/quote.step.compare.checkmonster';
import { QuoteStepCompareH2H } from './service/quote/quote.step.compare.h2h';
import { QuoteStepCompareUnderwriting } from './service/quote/quote.step.compare.underwriting';
import { QuoteStepReport } from './service/quote/quote.step.compare.report';
import { UserService } from './service/user.service';
import { UserSecurityService } from './service/user.security.service';
import { SharedFunctionService } from './service/shared.function.service';
import { NeedAnalysisService } from './service/need.analysis.service';
import { ResizeService } from './service/screen.resize.service';
import { CanDeactivateGuard } from './components/shared/can-deactivate-guard.service';
import { ConfirmMessageDialogService } from './components/shared/confirm-message-dialog.service';
import { ApiService } from './service/api.service';
import { UserMenuService } from './service/user.menu.service';
import { BusinessService } from './service/business/business.service';
import { BusinessDetailService } from './service/business/business.detail.service';
import { BusinessPeopleService } from './service/business/business.people.service';
import { BusinessFinancialPositionService } from './service/business/business.financial.position.service';
import { BusinessFinancialPerformanceService } from './service/business/business.financial.performance.service';
import { BusinessExistingCoversService } from './service/business/business.existing.covers.service';
import { BusinessQuoteService } from './service/business/quote/business.quote.service';
import { BusinessNcNatureService } from './service/business/business-need-analysis/business.nc.nature.service';
import { BusinessNcObjectivesService } from './service/business/business-need-analysis/business.nc.objectives.service';
import { BusinessNcScopePrioritiesService } from './service/business/business-need-analysis/business.nc.scope.priorities.service';
import { BusinessNcCalculationsService } from './service/business/business-need-analysis/business.nc.calculations.service';
// Datadog
import { datadogRum } from '@datadog/browser-rum';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      CommonModule,
      FormsModule,
      BrowserModule,
      ReactiveFormsModule,
      CurrencyMaskModule,
      MatAutocompleteModule,
      MatButtonModule,
      MatButtonToggleModule,
      MatCardModule,
      MatCheckboxModule,
      MatChipsModule,
      MatDatepickerModule,
      MatDialogModule,
      MatExpansionModule,
      MatFormFieldModule,
      MatGridListModule,
      MatIconModule,
      MatInputModule,
      MatListModule,
      MatMenuModule,
      MatNativeDateModule,
      MatPaginatorModule,
      MatProgressBarModule,
      MatProgressSpinnerModule,
      MatRadioModule,
      MatRippleModule,
      MatSelectModule,
      MatSidenavModule,
      MatSliderModule,
      MatSlideToggleModule,
      MatSnackBarModule,
      MatSortModule,
      MatTableModule,
      MatTabsModule,
      MatToolbarModule,
      MatTooltipModule,
      MatBadgeModule,
      MatStepperModule,
      A11yModule,
      BidiModule,
      ObserversModule,
      OverlayModule,
      PlatformModule,
      PortalModule,
      MatBottomSheetModule,
      DragDropModule,
      MatSidenavModule,
      EditorModule,
      MatTreeModule
    ),
    // QM
    FeedbackService,
    QmadsService,
    ApiAddressService,
    SysConfigService,
    LoginService,
    BlogService,
    DataLoaderService,
    DataLoader,
    QuoteService,
    QuoteStepClient,
    QuoteStepBenefit,
    QuoteStepCompare,
    QuoteStepCompareChart,
    QuoteStepCompareDocument,
    QuoteStepCompareResearch,
    QuoteStepCompareCheckmonster,
    QuoteStepCompareH2H,
    QuoteStepCompareUnderwriting,
    QuoteStepReport,
    UserService,
    UserSecurityService,
    MatStepperIntl,
    SharedFunctionService,
    NeedAnalysisService,
    ResizeService,
    CanDeactivateGuard,
    ConfirmMessageDialogService,
    ApiService,
    UserMenuService,
    DatePipe,
    BusinessService,
    BusinessDetailService,
    BusinessPeopleService,
    BusinessFinancialPositionService,
    BusinessFinancialPerformanceService,
    BusinessExistingCoversService,
    BusinessQuoteService,
    BusinessNcNatureService,
    BusinessNcObjectivesService,
    BusinessNcScopePrioritiesService,
    BusinessNcCalculationsService,
    {
      provide: UrlSerializer,
      useClass: LowerCaseUrlSerializer,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-NZ',
    },
    {
      provide: TINYMCE_SCRIPT_SRC,
      useValue: 'tinymce/tinymce.min.js',
    },
    // HttpClient
    provideHttpClient(withInterceptorsFromDi()),
    // Animations
    provideAnimationsAsync(),
    provideAppInitializer(initDatadog),
    provideRouter(
      appRoutes,
      // 1. Preloading Strategy
      withPreloading(PreloadAllModules),

      // 2. Initial Navigation: 'enabledBlocking'
      withEnabledBlockingInitialNavigation(),

      // 3. Scrolling options (anchorScrolling & scrollPositionRestoration)
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      })

      // useHash: false is the default. To enable hash location strategy, add withHashLocation().
      // enableTracing: false is the default. To enable route tracing for debugging, add withDebugTracing().
    ),
  ],
};

// Init Datadog
export function initDatadog(): void {
  datadogRum.init({
    applicationId: '27471101-5f2b-4dd0-8f32-6bbbb8f3eed6',
    clientToken: 'pubba8534ff57eef8d054ad24f4ba8ec412',
    site: 'ap1.datadoghq.com',
    service: 'qmfrontend',
    env: isDevMode() ? 'dev' : 'prod',
    version: environment.webVersion,
    allowedTracingUrls: [
      'https://quotemonster.co.nz',
      'https://*.quotemonster.co.nz',
      'https://kiwimonster.co.nz',
      'https://*.kiwimonster.co.nz',
    ],
    sessionSampleRate: 100,
    sessionReplaySampleRate: 20,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });
}
